import React, { FunctionComponent, useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { FormProvider, useForm } from 'react-hook-form';
import { path, pathOr } from 'ramda';
import withStyles from 'react-jss';
import { Button, Card, Form, Grid, Header, Message } from 'semantic-ui-react';
import classnames from 'classnames';

import Loading from 'components/Loading';
import FormInput from 'components/FormComponents/FormInput';
import EmailInput from 'components/FormComponents/EmailInput';
import { ClassProp } from 'helpers/types';
import {
  Merchant,
  userTypes,
  userRoles,
  merchantTransformer,
} from 'components/UserMgmt/UserMgmt.helpers';
import { GET_MERCHANTS } from 'components/UserMgmt/UserMgmt.queries';
import styles from 'components/UserMgmt/UserMgmt.styles';
import FormSelect from 'components/FormComponents/FormSelect';

import { CREATE_USER } from './signUp.queries';

export type Values = {
  givenName: string;
  familyName: string;
  email: string;
  merchant: {
    merchantId: string;
    merchantName: string;
  };
  userType: string;
  userRole: string;
};

type CreateUser = {
  userPoolId: string;
  userName: string;
  desiredDeliveryMediums: 'EMAIL' | 'SMS';
  userAttributes: [
    {
      name: string;
      value: string;
    },
  ];
  validationData: [
    {
      name: string;
      value: string;
    },
  ];
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

type SignupForm = {
  merchant: string;
  email: string;
  givenName: string;
  familyName: string;
  userType: string;
  userRole: string;
};

type Status = {
  errors?: string;
  success: boolean;
  successMsg?: string;
};

const initialStatus: Status = {
  errors: undefined,
  success: false,
};

const defaultValues = {
  givenName: '',
  familyName: '',
  email: '',
  merchant: '',
};

const SignUp: FunctionComponent<Props> = ({ classes }) => {
  const [createUser] = useMutation<{ createUser: CreateUser }>(CREATE_USER);
  const [status, setStatus] = useState<Status>(initialStatus);
  const { data, loading } = useQuery(GET_MERCHANTS, {
    variables: {
      params: {
        pagingInput: {
          offset: 0,
          limit: 800,
        },
      },
    },
  });

  const methods = useForm<SignupForm>({
    mode: 'onBlur',
    defaultValues,
  });

  const { handleSubmit, reset, register, formState } = methods;

  useEffect(() => {
    register('merchant', {
      required: 'Merchant field is required.',
    });
    register('userType');
    register('userRole');
  }, []);

  const onSubmit = async ({
    merchant,
    email,
    givenName,
    familyName,
    userType,
    userRole,
  }: SignupForm) => {
    setStatus({ ...status });
    try {
      const { merchantId, merchantName } = JSON.parse(merchant);
      const normalizedEmail = email.toLowerCase();
      const params = {
        userPoolId: process.env.COGNITO_USER_POOL_ID,
        userName: normalizedEmail,
        userAttributes: [
          {
            name: 'given_name',
            value: givenName,
          },
          {
            name: 'family_name',
            value: familyName,
          },
          {
            name: 'email',
            value: normalizedEmail,
          },
          {
            name: 'custom:merchantId',
            value: merchantId,
          },
          {
            name: 'custom:merchantName',
            value: merchantName,
          },
          {
            name: 'email_verified',
            value: 'true',
          },
          {
            name: 'custom:userType',
            value: userType,
          },
          {
            name: 'custom:userRole',
            value: userRole,
          },
        ],
        validationData: [],
        desiredDeliveryMediums: ['EMAIL'],
      };
      const response = await createUser({
        variables: { payload: { user: { ...params } } },
      });
      const successMsg = pathOr(
        [],
        ['data', 'createUser', 'success'],
        response,
      );
      setStatus({
        success: true,
        errors: undefined,
        successMsg: successMsg.message,
      });
    } catch (error) {
      setStatus({
        success: false,
        errors: error.message,
      });
    }
  };

  const merchants = pathOr([], ['merchantList', 'result'], data) as Merchant[];
  const merchantOptions = {
    options: merchantTransformer(merchants),
    placeholder: 'Merchants',
    label: {
      children: 'Select',
    },
  };

  const { errors, success } = status;

  const doReset = () => reset(defaultValues);

  return (
    <Grid className={classnames(classes.grid, classes.signUpForm)}>
      <Grid.Column className={classes.column}>
        <Header className={classes.header} as="h1">
          Sign Up New User
        </Header>
        <Card className={classes.container} fluid centered>
          <Card.Content>
            {formState.isSubmitting && <Loading loading />}
            <FormProvider {...methods}>
              <Form onSubmit={handleSubmit(onSubmit)} error={!!errors} success>
                {errors && !success && (
                  <Message error content={path(['server'], errors)} />
                )}

                {path(['successMsg'], status) && success && (
                  <Message
                    success
                    content={path(['successMsg'], status)}
                    onDismiss={doReset}
                  />
                )}
                <Form.Field className={classes.field}>
                  <FormSelect
                    label="Merchant"
                    loading={loading}
                    dropDownOptions={merchantOptions}
                    search
                    name="merchant"
                  />
                </Form.Field>
                <Form.Field className={classes.field}>
                  <FormInput
                    label="First Name"
                    name="givenName"
                    type="text"
                    placeholder="Enter your First Name"
                    register={register({ required: 'First Name not be blank' })}
                  />
                </Form.Field>

                <Form.Field className={classes.field}>
                  <FormInput
                    label="Last Name"
                    name="familyName"
                    type="text"
                    placeholder="Enter your Last Name"
                    register={register({
                      required: 'Last Name may not be blank',
                    })}
                  />
                </Form.Field>

                <Form.Field className={classes.field}>
                  <EmailInput
                    label="Email"
                    name="email"
                    placeholder="Enter your Email"
                  />
                </Form.Field>

                <Form.Field className={classes.field}>
                  <FormSelect
                    label={userTypes.label.children}
                    dropDownOptions={userTypes}
                    name="userType"
                  />
                </Form.Field>

                <Form.Field className={classes.field}>
                  <FormSelect
                    label={userRoles.label.children}
                    dropDownOptions={userRoles}
                    name="userRole"
                  />
                </Form.Field>

                <Form.Field>
                  <Button
                    fluid
                    type="submit"
                    className={classes.button}
                    disabled={!formState.isValid || formState.isSubmitting}
                  >
                    Sign Up
                  </Button>
                </Form.Field>
              </Form>
            </FormProvider>
          </Card.Content>
        </Card>
      </Grid.Column>
    </Grid>
  );
};

export default withStyles(styles)(SignUp);
